import { ApiController } from '@/services/ApiController';
import { ApiRoutes } from '@/enums/api/ApiRoutes';
import { AxiosResponse } from 'axios';
import AddressConfig from '@/models/interfaces/Address';

export default class AddressAPI {
    public static async getAll() {
        let addresses;

        try {
            addresses = (await ApiController.get(ApiRoutes.address.get)) as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }

        return Promise.resolve(addresses);
    }

    public static async getById(addressId: string) {
        let address;

        try {
            address = (await ApiController.get(ApiRoutes.address.getById(addressId))) as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }

        return Promise.resolve(address);
    }

    public static async update(addressId: string, addressInfo: AddressConfig, withRelationships = false) {
        let address;
        const attributes: any = {};

        // Only add present attributes
        if (addressInfo?.name) attributes.name = addressInfo.name;
        if (addressInfo?.streetNameAndNumber) attributes.streetNameAndNumber = addressInfo.streetNameAndNumber;
        if (addressInfo?.poNumber) attributes.poNumber = addressInfo.poNumber;
        if (addressInfo?.city) attributes.city = addressInfo.city;
        if (addressInfo?.building) attributes.building = addressInfo.building;
        if (addressInfo?.contactPerson) attributes.contactPerson = addressInfo.contactPerson;
        if (addressInfo?.note) attributes.note = addressInfo.note;
        if (addressInfo?.phone) attributes.phone = addressInfo.phone;
        attributes.isDefault = addressInfo?.isDefault || false;
        if (addressInfo?.b2bDeliveryPhone) attributes.b2bDeliveryPhone = addressInfo.b2bDeliveryPhone;
        if (addressInfo?.b2bDeliveryEmail) attributes.b2bDeliveryEmail = addressInfo.b2bDeliveryEmail;
        if (addressInfo?.b2bDeliveryNotes) attributes.b2bDeliveryNotes = addressInfo.b2bDeliveryNotes;
        if (addressInfo?.b2bDeliveryDays) attributes.b2bDeliveryDays = addressInfo.b2bDeliveryDays;
        if (addressInfo?.languageCode !== undefined) attributes.languageCode = addressInfo.languageCode;
        if (addressInfo?.printRegion !== undefined) attributes.printRegion = addressInfo.printRegion;

        const request: any = {
            data: {
                type: 'addresses',
                id: addressId,
                attributes,
                relationships: {},
            },
        };

        if (addressInfo.country) {
            request.data.relationships.country = {
                data: {
                    type: 'countries',
                    id: addressInfo.country,
                },
            };
        }

        if (addressInfo.addressType) {
            request.data.relationships.addressType = {
                data: {
                    type: 'address_types',
                    id: addressInfo.addressType,
                },
            };
        }

        if (addressInfo.parentRelationshipName) {
            request.data.relationships = {
                ...request.data.relationships,
                [addressInfo.parentRelationshipName]: addressInfo.parentRelationshipPayload,
            };
        }

        try {
            address = (await ApiController.patch(
                ApiRoutes.address.update(addressId, withRelationships),
                request,
            )) as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }

        return Promise.resolve(address);
    }

    public static async delete(addressId: string) {
        let address;

        try {
            address = (await ApiController.delete(ApiRoutes.address.update(addressId, false))) as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }

        return Promise.resolve(address);
    }

    public static async create(addressInfo: AddressConfig, withRelationships = false) {
        let address;
        const attributes: any = {};

        // Only add present attributes
        if (addressInfo?.name) attributes.name = addressInfo.name;
        if (addressInfo?.streetNameAndNumber) attributes.streetNameAndNumber = addressInfo.streetNameAndNumber;
        if (addressInfo?.poNumber) attributes.poNumber = addressInfo.poNumber;
        if (addressInfo?.city) attributes.city = addressInfo.city;
        if (addressInfo?.building) attributes.building = addressInfo.building;
        if (addressInfo?.contactPerson) attributes.contactPerson = addressInfo.contactPerson;
        if (addressInfo?.note) attributes.note = addressInfo.note;
        if (addressInfo?.phone) attributes.phone = addressInfo.phone;
        attributes.isDefault = addressInfo?.isDefault || false;
        if (addressInfo?.b2bDeliveryPhone) attributes.b2bDeliveryPhone = addressInfo.b2bDeliveryPhone;
        if (addressInfo?.b2bDeliveryEmail) attributes.b2bDeliveryEmail = addressInfo.b2bDeliveryEmail;
        if (addressInfo?.b2bDeliveryNotes) attributes.b2bDeliveryNotes = addressInfo.b2bDeliveryNotes;
        if (addressInfo?.b2bDeliveryDays) attributes.b2bDeliveryDays = addressInfo.b2bDeliveryDays;
        if (addressInfo?.languageCode !== undefined) attributes.languageCode = addressInfo.languageCode;
        if (addressInfo?.printRegion !== undefined) attributes.printRegion = addressInfo.printRegion;

        const request: any = {
            data: {
                type: 'addresses',
                attributes,
                relationships: {},
            },
        };

        if (addressInfo.country) {
            request.data.relationships.country = {
                data: {
                    type: 'countries',
                    id: addressInfo.country,
                },
            };
        }

        if (addressInfo.addressType) {
            request.data.relationships.addressType = {
                data: {
                    type: 'address_types',
                    id: addressInfo.addressType,
                },
            };
        }

        if (addressInfo.parentRelationshipName) {
            request.data.relationships = {
                ...request.data.relationships,
                [addressInfo.parentRelationshipName]: addressInfo.parentRelationshipPayload,
            };
        }

        try {
            address = await ApiController.post(ApiRoutes.address.create(withRelationships), request);
        } catch (e) {
            return Promise.reject(e);
        }

        return Promise.resolve(address);
    }

    public static async search(query: string) {
        let addresses;

        try {
            addresses = (await ApiController.get(ApiRoutes.address.searchAddresses(query))) as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }

        return Promise.resolve(addresses);
    }

    public static async getByClientID(clientId: string) {
        let addresses;

        try {
            addresses = (await ApiController.get(ApiRoutes.address.getByClientID(clientId))) as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }

        return Promise.resolve(addresses);
    }
}
